// ===========================================
// PWA Install Prompt Component
// Shows after 3+ visits, respects 7-day dismiss cooldown
// ===========================================

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Slide,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  Speed as SpeedIcon,
  Notifications as NotificationsIcon,
  CloudOff as CloudOffIcon,
  TouchApp as TouchAppIcon,
} from '@mui/icons-material';
import { forwardRef } from 'react';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';

// Slide transition for dialog
const SlideTransition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface InstallPromptProps {
  /** Force show the dialog (for testing or manual trigger) */
  forceShow?: boolean;
  /** Called when install succeeds */
  onInstalled?: () => void;
  /** Called when user dismisses */
  onDismissed?: () => void;
}

export default function InstallPrompt({
  forceShow = false,
  onInstalled,
  onDismissed,
}: InstallPromptProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isInstalling, setIsInstalling] = useState(false);

  // Visibility is controlled by PwaGate (mount/unmount), not the enabled param.
  // The hook runs unconditionally here; PwaGate ensures this component only
  // mounts on login and authenticated portal routes.
  const {
    shouldShowPrompt,
    canInstall,
    isInstalled,
    handleInstall,
    handleDismiss,
  } = useInstallPrompt();

  // Determine if dialog should be open
  const isOpen = (forceShow || shouldShowPrompt) && canInstall && !isInstalled;

  const onInstallClick = async () => {
    setIsInstalling(true);
    try {
      const success = await handleInstall();
      if (success && onInstalled) {
        onInstalled();
      }
    } finally {
      setIsInstalling(false);
    }
  };

  const onDismissClick = () => {
    handleDismiss();
    if (onDismissed) {
      onDismissed();
    }
  };

  const benefits = [
    {
      icon: <TouchAppIcon />,
      primary: 'Quick Access',
      secondary: 'Launch directly from your home screen',
    },
    {
      icon: <CloudOffIcon />,
      primary: 'Works Offline',
      secondary: 'View schedules even without internet',
    },
    {
      icon: <NotificationsIcon />,
      primary: 'Push Notifications',
      secondary: 'Get reminders for lessons and payments',
    },
    {
      icon: <SpeedIcon />,
      primary: 'Faster Loading',
      secondary: 'Cached content loads instantly',
    },
  ];

  return (
    <Dialog
      open={isOpen}
      onClose={onDismissClick}
      TransitionComponent={SlideTransition}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          m: isMobile ? 0 : 2,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GetAppIcon sx={{ color: '#4580E4' }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Install Music 'n Me
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onDismissClick}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Logo and description */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Box
            component="img"
            src="/music-n-me-logo.png"
            alt="Music 'n Me Logo"
            sx={{
              width: 80,
              height: 80,
              objectFit: 'contain',
              mb: 2,
            }}
          />
          <Typography variant="body1" color="text.secondary">
            Install our app for the best experience on your device
          </Typography>
        </Box>

        {/* Benefits list */}
        <List disablePadding>
          {benefits.map((benefit, index) => (
            <ListItem
              key={index}
              disableGutters
              sx={{ py: 1 }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: '#4580E4',
                }}
              >
                {benefit.icon}
              </ListItemIcon>
              <ListItemText
                primary={benefit.primary}
                secondary={benefit.secondary}
                primaryTypographyProps={{
                  variant: 'subtitle2',
                  fontWeight: 600,
                }}
                secondaryTypographyProps={{
                  variant: 'body2',
                }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onDismissClick}
          color="inherit"
          sx={{ flex: 1 }}
        >
          Not Now
        </Button>
        <Button
          onClick={onInstallClick}
          variant="contained"
          disabled={isInstalling}
          startIcon={<GetAppIcon />}
          sx={{
            flex: 2,
            backgroundColor: '#4580E4',
            '&:hover': {
              backgroundColor: '#3670d4',
            },
          }}
        >
          {isInstalling ? 'Installing...' : 'Install App'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
